import dynamic from "next/dynamic";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";

import { useContext, useEffect } from "react";
import { jsonLdScriptProps } from "react-schemaorg";

import CustomCard from "components/CustomCard";
import PaneLayout from "components/PaneLayout";
import SignUpForm from "components/SignUpForm";
import { TopNavBarContext } from "components/TopNavBar/TopNavBarContext";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { useGlobalState } from "hooks/useGlobalState";
import useMediaQuery from "hooks/useMediaQuery";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import { emberAppBaseUrl } from "utils/envVariables";
import { jsonLDForCollection } from "utils/jsonLD";

import CollectionPageDiscussionsWidget from "./CollectionPageDiscussionsWidget";
import CollectionPageHeader from "./CollectionPageHeader";
import CollectionPageRelatedCollections from "./CollectionPageRelatedCollections";
import CollectionPageRelatedIP from "./CollectionPageRelatedIP";
import CollectionPageTabs from "./CollectionPageTabs";
import styles from "./style.module.scss";

type CollectionPageLayoutProps = {
  slug: string;
  children: React.ReactNode;
  hideRightPane?: boolean;
};

const DianomiAd = dynamic(() => import("components/DianomiAd"), {
  ssr: false,
});

export const collectionUrl = (slug: string) => {
  const url = new ApiEndpoint(`/api/v2/collections/${slug}`);
  const include: string[] = [];
  url.searchParams.append("include", include.join(","));
  return url.href;
};

const CollectionPageLayout = ({
  slug,
  children,
  hideRightPane,
}: CollectionPageLayoutProps) => {
  const { setShowBackButton } = useContext(TopNavBarContext);

  const media = useMediaQuery();
  const router = useRouter();

  const { authenticated } = useAuthentication();

  const [signupSource, setSignupSource] = useGlobalState("signupSource");

  const { data: collection } = useApi(collectionUrl(slug), baseFetcher, {
    onSuccess: (data: any) => {
      setSignupSource({
        type: "Collection",
        id: data.id,
      });
    },
  });

  const coverImageUrl = collection?.coverImageUrl?.xlarge;

  const canonicalUrl =
    emberAppBaseUrl + (router.asPath === "/" ? "" : router.asPath);

  useEffect(() => {
    setShowBackButton(false);
  }, [setShowBackButton]);

  useEffect(() => {
    if (collection && collection.slug !== slug) {
      router.replace(
        router.asPath.replace(`/${slug}/`, `/${collection.slug}/`)
      );
    }
  }, [collection, router, slug]);

  useEffect(() => {
    if (
      collection &&
      signupSource?.id !== collection?.id &&
      location.pathname.startsWith("/collections")
    ) {
      setSignupSource({
        type: "Collection",
        id: collection?.id,
      });
    }
  }, [collection, setSignupSource, signupSource?.id]);

  return (
    <>
      <Head>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />

        {/* Open graph tags for social media etc */}
        <meta property="og:image" content={coverImageUrl} />
        <meta property="og:image:width" content="200" />
        <meta property="og:image:height" content="200" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <script
          {...jsonLdScriptProps(jsonLDForCollection({ collection }) as any)}
        ></script>
      </Head>
      {media.mobile ? (
        <div className={styles.mobileLayout}>
          <CustomCard className={styles.card}>
            {collection && <CollectionPageHeader {...{ collection }} />}
          </CustomCard>
          <CollectionPageTabs slug={slug} />
          <div className="bg-white mx-[-5px] mt-[15px]">{children}</div>
        </div>
      ) : (
        <PaneLayout
          className="px-[30px] py-[20px]"
          top={
            <>
              <div className="text-[16px] mb-[14px] hidden">
                <Link href="/collections" className="text-smart-teal">
                  Collections
                </Link>
                {" > "}
                <span>{collection?.name}</span>
              </div>
              <CustomCard
                additionalStyle={{ marginBottom: "15px", padding: 0 }}
              >
                {collection && <CollectionPageHeader {...{ collection }} />}
              </CustomCard>
            </>
          }
          left={<CollectionPageTabs slug={slug} />}
          right={
            <div className={"mt-[45px] py-[15px]"}>
              <DianomiAd type="right_sidebar" />
              {authenticated ? (
                <>
                  {!router.route.endsWith("/discussions") && (
                    <CollectionPageDiscussionsWidget widget />
                  )}
                </>
              ) : (
                !router.route.endsWith("/discussions") && (
                  <CustomCard className={styles.card}>
                    <SignUpForm collectionId={collection?.id} type="compact" />
                  </CustomCard>
                )
              )}
              {collection && (
                <CollectionPageRelatedIP collection={collection} widget />
              )}
              <CollectionPageRelatedCollections widget />
            </div>
          }
          hideRight={hideRightPane}
          hideActionBar={hideRightPane}
        >
          <div className={styles.entityDetails}>{children}</div>
        </PaneLayout>
      )}
    </>
  );
};

export default CollectionPageLayout;
